import React, { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Search, Filter, Sparkles, Download, Eye, Heart, Star, Share2, Bookmark, X, 
  ChevronRight, ChevronLeft, ArrowUpDown, LayoutGrid, Layers, FolderHeart, Trash2, 
  ZoomIn, ZoomOut, Maximize, Minimize, Info, Clock, User, Tag, ThumbsUp, ShieldCheck, 
  Flame, Compass, FileText, BarChart3, Bot, Globe, Briefcase, Award, Palette, Smartphone, 
  DollarSign, Target, Milestone, MessageSquare, Check, ExternalLink, Sparkle, Grid, AlignLeft,
  ArrowLeft, Upload, Plus, UploadCloud, RotateCcw, ImageIcon
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getDynamicTemplates, addUploadedTemplate, ALL_CATEGORIES } from "../data/templatesData";

// Helper for local storage of user-edited custom templates
const CUSTOM_TEMPLATES_KEY = "studlyf_custom_templates";
const getCustomTemplates = (userId) => {
  try {
    const raw = localStorage.getItem(`${CUSTOM_TEMPLATES_KEY}_${userId || "guest"}`);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};
const saveCustomTemplates = (userId, templatesList) => {
  try {
    localStorage.setItem(`${CUSTOM_TEMPLATES_KEY}_${userId || "guest"}`, JSON.stringify(templatesList));
  } catch (e) {
    console.error("Failed saving custom templates", e);
  }
};

const STAGES = ["All Stages", "Idea", "MVP", "Pre-Seed", "Seed", "Series A", "Series B"];
const DIFFICULTIES = ["All Difficulties", "Beginner", "Intermediate", "Advanced"];

// Category Icons Mapping
const getCategoryIcon = (category) => {
  switch (category) {
    case "Startup": case "Pitch Decks": return <Flame className="w-4 h-4 text-amber-500" />;
    case "Business": case "Business Plans": return <Briefcase className="w-4 h-4 text-blue-500" />;
    case "Marketing": case "Marketing Plans": case "Social Media Posts": return <Target className="w-4 h-4 text-pink-500" />;
    case "Finance": case "Financial Models": return <DollarSign className="w-4 h-4 text-emerald-500" />;
    case "Sales": case "Sales Decks": return <BarChart3 className="w-4 h-4 text-indigo-500" />;
    case "Education": case "Resume Templates": return <Award className="w-4 h-4 text-rose-500" />;
    case "Technology": case "AI Agent Templates": return <Bot className="w-4 h-4 text-purple-500" />;
    case "Product": case "Product Roadmap": return <Milestone className="w-4 h-4 text-cyan-500" />;
    case "Healthcare": return <ShieldCheck className="w-4 h-4 text-teal-500" />;
    case "Sustainability": return <Compass className="w-4 h-4 text-green-500" />;
    case "HR": case "HR Documents": return <User className="w-4 h-4 text-amber-600" />;
    case "Portfolio": case "Portfolio Websites": return <Palette className="w-4 h-4 text-violet-500" />;
    default: return <Sparkles className="w-4 h-4 text-purple-400" />;
  }
};

const getCategoryFallbackImage = (category = "") => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("startup") || cat.includes("pitch")) return "/templates/previews/pitch-deck-slide1.png";
  if (cat.includes("business") || cat.includes("corporate")) return "/templates/previews/user-template-corporate.png";
  if (cat.includes("marketing") || cat.includes("campaign")) return "/templates/previews/user-template-marketing.png";
  if (cat.includes("finance") || cat.includes("revenue")) return "/templates/previews/user-template-finance.png";
  if (cat.includes("sales") || cat.includes("client")) return "/templates/previews/pitch-deck-slide2.png";
  if (cat.includes("tech") || cat.includes("ai") || cat.includes("software")) return "/templates/previews/user-template-social.png";
  if (cat.includes("product") || cat.includes("roadmap")) return "/templates/previews/marketing-plan-slide1.png";
  if (cat.includes("education") || cat.includes("academic")) return "/templates/previews/studlyf-master-deck-slide2.png";
  if (cat.includes("health") || cat.includes("medical")) return "/templates/previews/legal-founders-agreement.png";
  if (cat.includes("sustain") || cat.includes("esg") || cat.includes("green")) return "/templates/previews/studlyf-master-deck-slide3.png";
  if (cat.includes("hr") || cat.includes("people")) return "/templates/previews/linkedin-thought-leadership.png";
  if (cat.includes("portfol")) return "/templates/previews/studlyf-master-deck-slide4.png";
  return "/templates/previews/user-template-corporate.png";
};

// Dynamic Template Preview Image Component
const TemplateImage = ({ src, title, category, structureType, className = "" }) => {
  const [imageError, setImageError] = useState(false);
  const fallbackSrc = getCategoryFallbackImage(category);
  const displaySrc = (!imageError && src) ? src : fallbackSrc;

  return (
    <div className={`w-full h-full bg-slate-950 flex items-center justify-center p-1 relative overflow-hidden group/img ${className}`}>
      <img 
        src={displaySrc} 
        alt={title || "Template Preview"} 
        loading="lazy"
        onError={() => setImageError(true)}
        className="w-full h-full object-contain rounded-xl transition-transform duration-500 ease-out group-hover/img:scale-[1.03] drop-shadow-md"
      />
    </div>
  );
};

export default function TemplatesPage({ isWorkspace = false }) {
  const { user, subscription } = useAuth();
  const isPro = Boolean(
    subscription?.plan_name === "STUDLYF Pro" ||
    subscription?.status === "active" ||
    subscription?.plan === "pro" ||
    user?.is_pro ||
    user
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Scroll Position Ref & History Stack
  const scrollPositionRef = useRef(0);
  const [detailHistory, setDetailHistory] = useState([]);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Templates");
  const [selectedIndustry, setSelectedIndustry] = useState("All Industries");
  const [selectedStage, setSelectedStage] = useState("All Stages");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All Difficulties");
  const [priceFilter, setPriceFilter] = useState("All");
  const [structureTypeFilter, setStructureTypeFilter] = useState("All Types");
  const [badgeFilter, setBadgeFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Most Popular");
  const [viewMode, setViewMode] = useState("grid"); // grid | masonry

  // State Collections initialized dynamically
  const [templates, setTemplates] = useState(() => getDynamicTemplates());
  const [customTemplates, setCustomTemplates] = useState([]);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [likedIds, setLikedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("All");
  const [visibleCount, setVisibleCount] = useState(12);

  // Dynamic Upload Modal States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("Pitch Decks");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadImagePreview, setUploadImagePreview] = useState(null);

  // Active Modals & Views
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [detailTemplate, setDetailTemplate] = useState(null);

  // Zoom & Image Viewer state inside Detail View
  const [detailZoomScale, setDetailZoomScale] = useState(1.0);
  const [detailSelectedImage, setDetailSelectedImage] = useState(null);

  // Presenter & Slide Editor States
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [zoomScale, setZoomScale] = useState(1.0);
  const [isPreviewWorkspaceFullscreen, setIsPreviewWorkspaceFullscreen] = useState(false);

  // Handle uploading file image locally in browser
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveUploadedTemplate = (e) => {
    e.preventDefault();
    if (!uploadTitle.trim() || !uploadImagePreview) {
      toast.error("Please enter a title and upload a template image!");
      return;
    }
    const created = addUploadedTemplate({
      title: uploadTitle.trim(),
      category: uploadCategory,
      description: uploadDescription.trim() || "Uploaded professional template design.",
      previewImage: uploadImagePreview,
      galleryImages: [uploadImagePreview]
    });
    if (created) {
      setTemplates(getDynamicTemplates());
      setIsUploadModalOpen(false);
      setUploadTitle("");
      setUploadDescription("");
      setUploadImagePreview(null);
      toast.success(`Successfully added template "${created.title}"!`, {
        description: "It has been dynamically rendered on the Templates page."
      });
    }
  };

  // Combine custom templates + database templates
  const allTemplates = useMemo(() => {
    return [...customTemplates, ...templates];
  }, [customTemplates, templates]);

  // Master Filter & Search Pipeline
  const filteredTemplates = useMemo(() => {
    return allTemplates.filter((tmpl) => {
      if (activeTab === "Popular") {
        if (!tmpl.isPopular && tmpl.downloads < 20000) return false;
      } else if (activeTab === "Featured") {
        if (!tmpl.isFeatured) return false;
      } else if (activeTab === "New Releases") {
        if (!tmpl.isNew) return false;
      } else if (activeTab === "My Copies") {
        if (!tmpl.isCustom) return false;
      }

      const q = searchTerm.toLowerCase().trim();
      const matchesSearch = !q || (
        tmpl.title.toLowerCase().includes(q) ||
        tmpl.category.toLowerCase().includes(q) ||
        tmpl.description.toLowerCase().includes(q) ||
        (tmpl.creator && tmpl.creator.toLowerCase().includes(q)) ||
        (tmpl.tags && tmpl.tags.some(t => t.toLowerCase().includes(q)))
      );

      const matchesCategory = selectedCategory === "All Templates" || 
        tmpl.category === selectedCategory ||
        (selectedCategory === "Pitch Decks" && (tmpl.category === "Startup" || tmpl.category === "Pitch Decks")) ||
        (selectedCategory === "Business Plans" && (tmpl.category === "Business" || tmpl.category === "Business Plans")) ||
        (selectedCategory === "Financial Models" && (tmpl.category === "Finance" || tmpl.category === "Financial Models")) ||
        (selectedCategory === "Marketing Plans" && (tmpl.category === "Marketing" || tmpl.category === "Marketing Plans")) ||
        (selectedCategory === "Social Media Posts" && (tmpl.category === "Marketing" || tmpl.category === "Social Media Posts")) ||
        (selectedCategory === "AI Agent Templates" && (tmpl.category === "Technology" || tmpl.category === "AI Agent Templates")) ||
        (selectedCategory === "Startup Landing Pages" && (tmpl.category === "Technology" || tmpl.category === "Startup Landing Pages")) ||
        (selectedCategory === "Resume Templates" && (tmpl.category === "Education" || tmpl.category === "Portfolio" || tmpl.category === "Resume Templates")) ||
        (selectedCategory === "Product Roadmap" && (tmpl.category === "Product" || tmpl.category === "Product Roadmap")) ||
        (selectedCategory === "Legal Documents" && (tmpl.category === "Healthcare" || tmpl.category === "Business" || tmpl.category === "Legal Documents"));
      const matchesIndustry = selectedIndustry === "All Industries" || (tmpl.industry && tmpl.industry.includes(selectedIndustry));
      const matchesStage = selectedStage === "All Stages" || (tmpl.stage && tmpl.stage.includes(selectedStage));
      const matchesDifficulty = selectedDifficulty === "All Difficulties" || tmpl.difficulty === selectedDifficulty;
      const matchesPrice = priceFilter === "All" || tmpl.price === priceFilter;
      const matchesStructureType = structureTypeFilter === "All Types" || tmpl.structureType === structureTypeFilter.toLowerCase();
      const matchesBadge = badgeFilter === "All" || 
        (badgeFilter === "Popular" && tmpl.isPopular) ||
        (badgeFilter === "New" && tmpl.isNew) ||
        (badgeFilter === "Featured" && tmpl.isFeatured);

      return matchesSearch && matchesCategory && matchesIndustry && matchesStage && matchesDifficulty && matchesPrice && matchesStructureType && matchesBadge;
    }).sort((a, b) => {
      if (sortBy === "Most Popular" || activeTab === "Popular") return (b.downloads || 0) - (a.downloads || 0);
      if (sortBy === "Most Liked") return (b.likes || 0) - (a.likes || 0);
      if (sortBy === "Highest Rated") return (b.rating || 0) - (a.rating || 0);
      if (sortBy === "Newest" || sortBy === "Recently Added") return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
      return 0;
    });
  }, [allTemplates, searchTerm, selectedCategory, selectedIndustry, selectedStage, selectedDifficulty, priceFilter, structureTypeFilter, badgeFilter, activeTab, sortBy]);

  // Load User Custom Templates
  useEffect(() => {
    if (user?.user_id) {
      setCustomTemplates(getCustomTemplates(user.user_id));
    }
  }, [user]);

  // Sync URL search params with detail template
  useEffect(() => {
    if (editingTemplate) return;
    const templateIdParam = searchParams.get("id") || searchParams.get("templateId");
    if (templateIdParam) {
      const found = allTemplates.find(t => t.id === templateIdParam);
      if (found && detailTemplate?.id !== found.id) {
        setDetailTemplate(found);
        setDetailSelectedImage(found.previewImage);
        setDetailZoomScale(1.0);
      }
    } else if (!templateIdParam && detailTemplate) {
      setDetailTemplate(null);
    }
  }, [searchParams, allTemplates, detailTemplate, editingTemplate]);

  // Save Custom Templates helper
  const updateCustomTemplatesState = (newList) => {
    setCustomTemplates(newList);
    if (user?.user_id) {
      saveCustomTemplates(user.user_id, newList);
    }
  };

  // Toggle Bookmark helper
  const toggleBookmark = (id) => {
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        toast.info("Removed from bookmarks");
      } else {
        next.add(id);
        toast.success("Saved to bookmarks!");
      }
      return next;
    });
  };

  // Toggle Like helper
  const toggleLike = (id) => {
    setLikedIds(prev => {
      const next = new Set(prev);
      const isLiked = next.has(id);
      if (isLiked) {
        next.delete(id);
      } else {
        next.add(id);
        toast.success("Added to favorites!");
      }
      setTemplates(prevList =>
        prevList.map(t => t.id === id ? { ...t, likes: (t.likes || 0) + (isLiked ? -1 : 1) } : t)
      );
      return next;
    });
  };

  // Share template link
  const handleShareTemplate = (tmpl) => {
    const url = `${window.location.origin}/templates?id=${tmpl.id}`;
    navigator.clipboard.writeText(url);
    toast.success(`Copied share link for "${tmpl.title}"!`);
  };

  // Modal Lifecycle & Continuous Navigation Helpers
  const handleOpenPreview = (tmpl) => {
    setEditingTemplate(null);
    setPreviewTemplate(tmpl);
    setActiveSlideIndex(0);
    setZoomScale(1.0);
    setIsPreviewWorkspaceFullscreen(false);
  };

  const handleClosePreview = () => {
    setPreviewTemplate(null);
    setActiveSlideIndex(0);
    setZoomScale(1.0);
  };

  const handleOpenDetail = (tmpl, pushHistory = true) => {
    if (!detailTemplate) {
      scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop || 0;
    }
    if (pushHistory && detailTemplate && detailTemplate.id !== tmpl.id) {
      setDetailHistory(prev => [...prev, detailTemplate]);
    }
    setDetailTemplate(tmpl);
    setDetailSelectedImage(tmpl.previewImage);
    setDetailZoomScale(1.0);
    setActiveSlideIndex(0);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("id", tmpl.id);
      return next;
    }, { replace: true });
  };

  const handleCloseDetail = () => {
    setDetailTemplate(null);
    setDetailHistory([]);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      next.delete("templateId");
      return next;
    }, { replace: true });
    setTimeout(() => {
      window.scrollTo({ top: scrollPositionRef.current, behavior: "instant" });
    }, 50);
  };

  const handleDetailGoBack = () => {
    if (detailHistory.length > 0) {
      const prevTmpl = detailHistory[detailHistory.length - 1];
      setDetailHistory(prev => prev.slice(0, prev.length - 1));
      handleOpenDetail(prevTmpl, false);
    } else {
      handleCloseDetail();
    }
  };

  // Next / Previous Navigation within filtered templates list
  const currentDetailIndex = useMemo(() => {
    if (!detailTemplate) return -1;
    return filteredTemplates.findIndex(t => t.id === detailTemplate.id);
  }, [detailTemplate, filteredTemplates]);

  const hasPrevDetail = currentDetailIndex > 0;
  const hasNextDetail = currentDetailIndex >= 0 && currentDetailIndex < filteredTemplates.length - 1;

  const handlePrevDetail = () => {
    if (hasPrevDetail) {
      const prevTmpl = filteredTemplates[currentDetailIndex - 1];
      handleOpenDetail(prevTmpl, true);
    }
  };

  const handleNextDetail = () => {
    if (hasNextDetail) {
      const nextTmpl = filteredTemplates[currentDetailIndex + 1];
      handleOpenDetail(nextTmpl, true);
    }
  };

  // Related & Similar templates for details view
  const relatedTemplates = useMemo(() => {
    if (!detailTemplate) return [];
    return allTemplates
      .filter(t => t.id !== detailTemplate.id && (t.category === detailTemplate.category || (t.tags && detailTemplate.tags && t.tags.some(tag => (detailTemplate.tags || []).includes(tag)))))
      .slice(0, 4);
  }, [detailTemplate, allTemplates]);

  const handleOpenEditor = (tmpl) => {
    setPreviewTemplate(null);
    setDetailTemplate(null);
    const tmplWithSlides = {
      ...tmpl,
      slides: (tmpl?.slides && tmpl.slides.length > 0) ? tmpl.slides : getSlidesForTemplate(tmpl)
    };
    setEditingTemplate(tmplWithSlides);
    setActiveSlideIndex(0);
    setZoomScale(1.0);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      next.delete("templateId");
      return next;
    }, { replace: true });
  };

  // Create Copy & Use Template Action
  const handleUseTemplate = async (tmpl) => {
    if (!user) {
      toast.error("Please log in to customize templates.");
      navigate("/login");
      return;
    }
    if (tmpl.price === "Premium" && !isPro) {
      toast.error("This is a premium template. Please upgrade to Pro to customize it!");
      navigate(isWorkspace ? "/workspace/settings?tab=billing" : "/#pricing");
      return;
    }

    setTemplates(prev =>
      prev.map(t => t.id === tmpl.id ? { ...t, downloads: (t.downloads || 0) + 1 } : t)
    );

    const newCopy = {
      ...tmpl,
      id: `copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: `${tmpl.title} (Copy)`,
      downloads: 1,
      rating: 5.0,
      isCustom: true,
      lastUpdated: new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    };

    const updatedList = [newCopy, ...customTemplates];
    updateCustomTemplatesState(updatedList);

    toast.success(`Created editable copy of ${tmpl.title}!`, {
      description: "Opened in Template Editor.",
      duration: 4000
    });

    handleClosePreview();
    handleCloseDetail();
    handleOpenEditor(newCopy);
  };

  // Delete local copy
  const deleteCustomTemplate = (id) => {
    const updated = customTemplates.filter(t => t.id !== id);
    updateCustomTemplatesState(updated);
    toast.success("Deleted template copy.");
  };

  // Save changes to custom template
  const saveCustomTemplateState = (tmplToSave) => {
    if (!tmplToSave) return;
    const updatedList = customTemplates.some(t => t.id === tmplToSave.id)
      ? customTemplates.map(t => t.id === tmplToSave.id ? tmplToSave : t)
      : [tmplToSave, ...customTemplates];
    updateCustomTemplatesState(updatedList);
  };

  const handleCloseEditor = () => {
    setEditingTemplate(null);
    setDetailTemplate(null);
    setPreviewTemplate(null);
    setActiveSlideIndex(0);
    setZoomScale(1.0);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      next.delete("templateId");
      return next;
    }, { replace: true });
    setTimeout(() => {
      window.scrollTo({ top: scrollPositionRef.current, behavior: "instant" });
    }, 50);
  };

  const handleSaveAndDownloadInEditor = (format) => {
    if (!editingTemplate) return;
    saveCustomTemplateState(editingTemplate);
    handleDownload(editingTemplate, format);
  };

  // Download Visible High-Resolution PNG Template Design Image
  const handleDownloadImage = (tmpl, slideIdx = activeSlideIndex) => {
    const targetTmpl = (editingTemplate && editingTemplate.id === tmpl?.id)
      ? editingTemplate
      : (customTemplates.find(t => t.id === tmpl?.id) || templates.find(t => t.id === tmpl?.id) || tmpl || detailTemplate || previewTemplate);

    if (!targetTmpl) return;

    const slidesList = getSlidesForTemplate(targetTmpl);
    const targetSlide = slidesList[slideIdx] || slidesList[0];
    const imgSrc = (typeof targetSlide?.image === "string" && targetSlide.image.startsWith("/"))
      ? targetSlide.image
      : (targetTmpl?.galleryImages?.[slideIdx] || targetTmpl?.previewImage || "/templates/previews/studlyf-master-deck.png");

    const link = document.createElement("a");
    link.href = imgSrc;
    link.download = `${targetTmpl.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_slide_${(slideIdx || 0) + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Downloaded high-res template design image for "${targetTmpl.title}"!`);
  };

  // Exporter (PDF / PPTX / PNG)
  const handleDownload = async (tmpl, format, slideIdx = activeSlideIndex) => {
    if (!user) {
      toast.error("Please log in to export templates.");
      navigate("/login");
      return;
    }
    if (!isPro) {
      toast.error("Export is a Pro-only feature. Please upgrade to Pro!");
      navigate(isWorkspace ? "/workspace/settings?tab=billing" : "/#pricing");
      return;
    }

    const targetTmpl = (editingTemplate && editingTemplate.id === tmpl?.id)
      ? editingTemplate
      : (customTemplates.find(t => t.id === tmpl?.id) || templates.find(t => t.id === tmpl?.id) || tmpl || detailTemplate || previewTemplate);

    if (!targetTmpl) return;

    if (format === "PNG" || format === "IMAGE") {
      handleDownloadImage(targetTmpl, slideIdx);
      return;
    }

    toast.info(`Preparing high-resolution ${format} export for "${targetTmpl.title}"...`);

    if (format === "PDF") {
      setTimeout(() => {
        window.print();
      }, 500);
    } else if (format === "PPTX") {
      try {
        if (!window.PptxGenJS) {
          const script = document.createElement("script");
          script.src = "https://cdn.jsdelivr.net/gh/gitbrent/pptxgenjs@3.12.0/dist/pptxgen.bundle.js";
          script.onload = () => buildAndSavePptx(targetTmpl);
          document.body.appendChild(script);
        } else {
          buildAndSavePptx(targetTmpl);
        }
      } catch (e) {
        toast.error("Failed creating PPTX presentation file.");
      }
    }
  };

  const buildAndSavePptx = (tmpl) => {
    try {
      const pptx = new window.PptxGenJS();
      pptx.layout = "LAYOUT_16x9";

      const slidesList = getSlidesForTemplate(tmpl);
      slidesList.forEach((s, idx) => {
        const slide = pptx.addSlide();
        slide.background = { color: "0F172A" };
        
        const imgPath = (typeof s.image === "string" && s.image.startsWith("/"))
          ? s.image
          : (tmpl?.galleryImages?.[idx] || tmpl?.previewImage || "/templates/previews/studlyf-master-deck.png");

        const fullImgUrl = imgPath.startsWith("http") ? imgPath : (window.location.origin + imgPath);

        try {
          slide.addImage({
            path: fullImgUrl,
            x: 0,
            y: 0,
            w: "100%",
            h: "100%"
          });
        } catch (e) {
          console.warn("Could not embed background image in PPTX slide", e);
        }

        if (s.title) {
          slide.addText(s.title, { 
            x: 0.4, 
            y: 0.3, 
            fontSize: 15, 
            bold: true, 
            color: "E9D5FF", 
            fill: { color: "0F172A", alpha: 15 },
            rectRadius: 0.1
          });
        }

        if (s.bullets) {
          slide.addText(s.bullets, { 
            x: 0.4, 
            y: 4.9, 
            w: 9.2, 
            h: 0.6, 
            fontSize: 11, 
            color: "CBD5E1", 
            fill: { color: "0F172A", alpha: 20 },
            rectRadius: 0.1
          });
        }
      });

      pptx.writeFile({ fileName: `${tmpl.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pptx` });
      toast.success(`Exported clean high-res PPTX presentation for "${tmpl.title}"!`);
    } catch (err) {
      console.error("Error generating PPTX file", err);
      toast.error("Error writing PPTX presentation.");
    }
  };

  // Convert template structure to slides presentation stream
  const getSlidesForTemplate = (tmpl) => {
    if (!tmpl) return [];

    const getIllustrationType = (idx) => {
      const types = ["analytics", "matrix", "steps", "growth"];
      return types[idx % types.length];
    };

    if (tmpl.slides && tmpl.slides.length > 0) {
      return tmpl.slides.map((slide, idx) => ({
        number: slide.number || (idx + 1),
        title: slide.title || "Slide Title",
        subtitle: slide.subtitle || tmpl.category || "Presentation",
        layoutType: slide.layoutType || (idx === 0 ? "hero" : idx === tmpl.slides.length - 1 ? "closing" : "two-column"),
        bullets: slide.bullet || slide.bullets || "",
        image: slide.image || getIllustrationType(idx),
        metrics: slide.metrics || [
          { value: "10x", label: "Efficiency Increase" },
          { value: "85%", label: "Time Saved" },
          { value: "24/7", label: "Active Support" }
        ],
        timeline: slide.timeline || [
          { label: "Phase 1: Setup", description: "Define objectives & MVP roadmap" },
          { label: "Phase 2: Build", description: "Deploy design assets & launch" },
          { label: "Phase 3: Scale", description: "Expand market acquisition channels" }
        ],
        columns: slide.columns || [
          { title: "Strategic Highlights", content: "Brief bullet or overview of the active phase." },
          { title: "Key Deliverables", content: "Actionable tasks to achieve this milestone." }
        ]
      }));
    }

    const slides = [];
    slides.push({
      number: 1,
      title: tmpl.title,
      subtitle: `${tmpl.category} | Estimated Time: ${tmpl.time || "30 mins"}`,
      layoutType: "hero",
      bullets: tmpl.description,
      image: "analytics"
    });

    slides.push({
      number: 2,
      title: "Executive Summary & Vision",
      subtitle: tmpl.title,
      layoutType: "standard-light",
      bullets: "• High-level strategic overview and core value proposition.\n• Immediate problem definition and target market size.\n• Operational benchmarks and execution roadmap.",
      image: "matrix"
    });

    slides.push({
      number: 3,
      title: "Key Deliverables & Action Plan",
      subtitle: tmpl.title,
      layoutType: "two-column",
      bullets: tmpl.description,
      columns: [
        { title: "Phase 1 Objectives", content: "• Core framework setup.\n• Initial market validation.\n• Infrastructure deployment." },
        { title: "Phase 2 Scale", content: "• Channel expansion.\n• Automated workflows.\n• Metric optimization." }
      ],
      image: "steps"
    });

    slides.push({
      number: 4,
      title: "Thank You",
      subtitle: "STUDLYF Templates Marketplace",
      layoutType: "closing",
      bullets: "Start customizing this blueprint to scale your operations today.",
      image: "growth"
    });

    return slides;
  };

  // Memoized slide streams
  const previewSlides = useMemo(() => getSlidesForTemplate(previewTemplate), [previewTemplate]);
  const editorSlides = useMemo(() => getSlidesForTemplate(editingTemplate), [editingTemplate]);
  const detailSlides = useMemo(() => getSlidesForTemplate(detailTemplate), [detailTemplate]);

  const activeTemplateId = previewTemplate?.id || editingTemplate?.id || detailTemplate?.id || null;

  useEffect(() => {
    setActiveSlideIndex(0);
    setZoomScale(1.0);
  }, [activeTemplateId]);

  // Handle Slide Text Editing inside Presenter Workspace
  const handleSlideTextChange = (field, value, index1 = null) => {
    if (!editingTemplate) return;

    if (editingTemplate.structureType === "slides" && editingTemplate.slides) {
      const newSlides = [...editingTemplate.slides];
      const slideIdx = activeSlideIndex;
      
      if (slideIdx < newSlides.length) {
        const targetSlide = { ...newSlides[slideIdx] };
        
        if (field === "title") {
          targetSlide.title = value;
          if (slideIdx === 0) setEditingTemplate(prev => ({ ...prev, title: value }));
        } else if (field === "bullets") {
          targetSlide.bullets = value;
          if (slideIdx === 0) setEditingTemplate(prev => ({ ...prev, description: value }));
        } else if (field === "column-title" && index1 !== null) {
          const newCols = [...(targetSlide.columns || [])];
          newCols[index1] = { ...newCols[index1], title: value };
          targetSlide.columns = newCols;
        } else if (field === "column-content" && index1 !== null) {
          const newCols = [...(targetSlide.columns || [])];
          newCols[index1] = { ...newCols[index1], content: value };
          targetSlide.columns = newCols;
        } else if (field === "metric-value" && index1 !== null) {
          const newMetrics = [...(targetSlide.metrics || [])];
          newMetrics[index1] = { ...newMetrics[index1], value: value };
          targetSlide.metrics = newMetrics;
        } else if (field === "metric-label" && index1 !== null) {
          const newMetrics = [...(targetSlide.metrics || [])];
          newMetrics[index1] = { ...newMetrics[index1], label: value };
          targetSlide.metrics = newMetrics;
        } else if (field === "timeline-label" && index1 !== null) {
          const newTimeline = [...(targetSlide.timeline || [])];
          newTimeline[index1] = { ...newTimeline[index1], label: value };
          targetSlide.timeline = newTimeline;
        } else if (field === "timeline-desc" && index1 !== null) {
          const newTimeline = [...(targetSlide.timeline || [])];
          newTimeline[index1] = { ...newTimeline[index1], description: value };
          targetSlide.timeline = newTimeline;
        }
        
        newSlides[slideIdx] = targetSlide;
        setEditingTemplate(prev => ({ ...prev, slides: newSlides }));
      }
      return;
    }

    if (activeSlideIndex === 0) {
      if (field === "title") setEditingTemplate(prev => ({ ...prev, title: value }));
      if (field === "description") setEditingTemplate(prev => ({ ...prev, description: value }));
    }
  };

  const totalCount = filteredTemplates.length;

  // Safe slide indices for presentation viewers
  const safePreviewIndex = Math.min(activeSlideIndex, Math.max(0, previewSlides.length - 1));
  const safeEditorIndex = Math.min(activeSlideIndex, Math.max(0, editorSlides.length - 1));
  const safeDetailIndex = Math.min(activeSlideIndex, Math.max(0, detailSlides.length - 1));

  const currentPreviewSlide = previewSlides[safePreviewIndex] || null;
  const currentEditorSlide = editorSlides[safeEditorIndex] || null;
  const currentDetailSlide = detailSlides[safeDetailIndex] || null;

  // Render Slide Canvas Element: displays the template design image clean, unblurred, and high-res without overlapping placeholder text
  const renderSlideCanvas = (slide, slideIdx, totalSlides, tmpl = null, forceEditable = null) => {
    if (!slide) return null;

    const targetTmpl = tmpl || detailTemplate || previewTemplate || editingTemplate;
    const isEditable = forceEditable !== null ? forceEditable : Boolean(editingTemplate && targetTmpl && editingTemplate.id === targetTmpl.id);
    const slideImgSrc = (typeof slide.image === "string" && slide.image.startsWith("/"))
      ? slide.image
      : (targetTmpl?.galleryImages?.[slideIdx] || targetTmpl?.previewImage || null);

    // Detect if user has entered explicit custom text edits
    const hasCustomUserEdits = Boolean(
      isEditable && (
        (slide.title && slide.title !== targetTmpl?.title && !slide.title.includes("Master") && !slide.title.includes("Pitch Deck") && slide.title !== "Click to edit Title") ||
        (slide.bullets && slide.bullets !== targetTmpl?.description && !slide.bullets.includes("STUDLYF professionally crafted"))
      )
    );

    return (
      <div className="w-full h-full bg-slate-950 text-white rounded-2xl overflow-hidden border border-slate-800 relative shadow-2xl flex flex-col justify-between p-4 sm:p-6 select-none group antialiased [text-rendering:optimizeLegibility] [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]">
        
        {/* Full-resolution Clean Template Design Image Frame */}
        {slideImgSrc ? (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-slate-950 p-2 z-0">
            <img 
              src={slideImgSrc} 
              alt={slide.title || "Template Design"} 
              className="w-full h-full object-contain rounded-xl drop-shadow-md transition-transform duration-500 group-hover:scale-[1.01] [image-rendering:-webkit-optimize-contrast]"
            />
          </div>
        ) : (
          <>
            {/* Background Decorative Accents for blank canvas */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-[#7C3AED]/25 to-pink-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-0 right-0 w-1/2 h-full bg-slate-900/60 skew-x-12 transform translate-x-16 border-l border-slate-800/80 pointer-events-none" />
          </>
        )}

        {/* Minimal Non-Overlapping Header Badge */}
        <div className="flex justify-between items-center z-10 pointer-events-none">
          <span className="text-[10px] sm:text-xs font-extrabold uppercase tracking-widest bg-slate-950/90 backdrop-blur-md text-purple-300 px-3.5 py-1 rounded-full border border-purple-800/60 shadow-md">
            {slide.subtitle || targetTmpl?.category || "Master Template"}
          </span>
          <span className="text-[10px] sm:text-xs font-bold text-slate-300 bg-slate-950/90 backdrop-blur-md px-3 py-1 rounded-full border border-slate-800 font-mono shadow-md">
            Slide {slideIdx + 1} / {totalSlides}
          </span>
        </div>

        {/* Render text overlay ONLY if no image exists OR if user entered explicit custom edits */}
        {(!slideImgSrc || hasCustomUserEdits) && (
          <div className="my-auto z-10 space-y-3.5 py-2 max-w-3xl w-full bg-slate-950/95 backdrop-blur-md p-5 sm:p-6 rounded-2xl border border-purple-500/40 shadow-2xl">
            {slide.title && (
              <h2 className="font-display text-xl sm:text-3xl font-black text-white leading-tight drop-shadow-md tracking-tight">
                {slide.title}
              </h2>
            )}
            {slide.bullets && (
              <p className="text-slate-100 text-sm sm:text-base leading-relaxed whitespace-pre-line font-medium drop-shadow-xs">
                {slide.bullets}
              </p>
            )}
          </div>
        )}

        {/* Locked STUDLYF Branding Footer Bar */}
        <div className="pt-2 border-t border-slate-800/80 flex justify-between items-center z-10 bg-slate-950/90 backdrop-blur-xs px-3 py-1 rounded-lg mt-auto select-none pointer-events-none">
          <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block animate-pulse" />
            © STUDLYF
          </span>
          <span className="text-[9px] sm:text-[10px] font-mono font-bold tracking-wider text-slate-400 uppercase bg-slate-900 border border-slate-800 px-2.5 py-0.5 rounded shadow-xs">
            Built with STUDLYF
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen flex flex-col ${!isWorkspace ? "bg-slate-50/50" : "bg-white"}`}>
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body * { visibility: hidden !important; }
          #print-presentation-container, #print-presentation-container * {
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          #print-presentation-container { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; }
        }
      ` }} />

      {/* Printable HD Container for PDF Export */}
      <div id="print-presentation-container" className="hidden print:block w-full">
        {(detailTemplate || previewTemplate || editingTemplate || templates[0]) && (
          <div className="space-y-6 p-4">
            {getSlidesForTemplate(detailTemplate || previewTemplate || editingTemplate || templates[0]).map((s, idx) => {
              const targetTmpl = detailTemplate || previewTemplate || editingTemplate || templates[0];
              return (
                <div key={idx} className="w-full aspect-[16/10] relative rounded-2xl overflow-hidden shadow-2xl bg-slate-950 p-8 text-white flex flex-col justify-between page-break-after-always">
                  <div className="flex justify-between items-center z-10">
                    <span className="bg-purple-950 text-purple-300 px-3.5 py-1 rounded-full text-xs font-extrabold border border-purple-800">
                      {s.subtitle || targetTmpl?.category || "Master Blueprint"}
                    </span>
                    <span className="bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-bold border border-slate-700">
                      Slide {idx + 1}
                    </span>
                  </div>

                  <div className="my-auto z-10 space-y-4 py-4 max-w-3xl">
                    <h2 className="font-display text-3xl font-black tracking-tight text-white leading-tight">
                      {s.title || "Untitled Slide"}
                    </h2>
                    {s.bullets && (
                      <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-line bg-slate-900/80 border border-slate-800 p-4 rounded-2xl">
                        {s.bullets}
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-800 flex justify-between items-center z-10 text-[10px] text-slate-400 font-mono">
                    <span>© STUDLYF</span>
                    <span className="font-bold uppercase tracking-wider bg-slate-900 border border-slate-800 px-2.5 py-0.5 rounded text-purple-300">
                      Built with STUDLYF
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={!isWorkspace ? "pt-24 flex-grow" : "flex-grow"}>
        <div className="pb-16 text-slate-900" data-testid="templates-page">
          
          {/* Background Ambient Glows */}
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl -z-10 pointer-events-none" />
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-pink-100/20 rounded-full blur-3xl -z-10 pointer-events-none" />

          {/* 1. HERO SECTION */}
          <div className="text-center pt-12 pb-10 w-full px-4 sm:px-6 md:px-8 max-w-6xl mx-auto relative overflow-hidden">
            <span className="text-[10px] tracking-widest uppercase font-bold text-[#7C3AED] bg-purple-50 border border-purple-200/60 px-3.5 py-1 rounded-full mb-4 inline-flex items-center gap-1.5 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" /> Templates Marketplace
            </span>
            <h1 className="font-display text-4xl md:text-5.5xl font-extrabold tracking-tighter leading-tight text-slate-900 mt-2">
              Blueprints for Every <span className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] bg-clip-text text-transparent">Startup Journey</span>
            </h1>
            <p className="mt-4 text-slate-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed font-normal">
              Browse professionally crafted templates for pitch decks, business plans, financial models, AI agent systems, landing pages, resumes, and executive reports.
            </p>

            {/* Global Search Bar */}
            <div className="mt-8 relative max-w-2xl mx-auto shadow-lg rounded-full bg-white border border-slate-200 p-1.5 flex items-center group transition focus-within:border-purple-400 focus-within:ring-4 focus-within:ring-purple-100">
              <div className="flex items-center flex-1 pl-5">
                <Search className="w-4 h-4 text-slate-400 mr-3 shrink-0 group-focus-within:text-[#7C3AED]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search pitch decks, AI agent templates, financial models, resumes..."
                  className="w-full text-xs outline-none text-slate-800 bg-transparent py-3 placeholder:text-slate-400"
                />
              </div>
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")} 
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-full shrink-0 mr-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Quick Stats Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-10">
              {[
                { title: "50+ Categories", label: "Industry Blueprints" },
                { title: "100% Customizable", label: "Native Presentation" },
                { title: "AI-Powered", label: "Instant Generation" },
                { title: "Multi-Export", label: "PDF, PPTX & Word" }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/80 backdrop-blur-xs border border-slate-200/80 rounded-2xl p-3.5 shadow-xs text-center">
                  <p className="text-xs font-black text-slate-900">{stat.title}</p>
                  <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 2. TOP CATEGORIES HUB BAR */}
          <div className="w-full px-4 sm:px-6 md:px-8 mb-8 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5 text-[#7C3AED]" /> Featured Categories
              </span>
              <span className="text-xs text-slate-400 font-medium">Showing {ALL_CATEGORIES.length - 1} categories</span>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-3 no-scrollbar scroll-smooth">
              {ALL_CATEGORIES.map((cat) => {
                const isSelected = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`rounded-2xl px-4 py-2 text-xs font-semibold shrink-0 transition flex items-center gap-2 border shadow-xs ${
                      isSelected
                        ? "bg-[#7C3AED] border-[#7C3AED] text-white shadow-md shadow-purple-200"
                        : "bg-white border-slate-200 text-slate-600 hover:border-purple-300 hover:text-slate-900"
                    }`}
                  >
                    {getCategoryIcon(cat)}
                    <span>{cat}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. MAIN MARKETPLACE LISTINGS & FILTER PANEL */}
          <div className="w-full px-4 sm:px-6 md:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-8">
            
            {/* Sidebar Controls Panel */}
            <div className="lg:col-span-1 space-y-5">
              <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-xs">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-[#7C3AED]" /> Marketplace Filters
                  </span>
                  {(selectedIndustry !== "All Industries" || selectedStage !== "All Stages" || selectedDifficulty !== "All Difficulties" || priceFilter !== "All" || structureTypeFilter !== "All Types" || badgeFilter !== "All" || selectedCategory !== "All Templates" || searchTerm) && (
                    <button
                      onClick={() => {
                        setSelectedCategory("All Templates");
                        setSelectedIndustry("All Industries");
                        setSelectedStage("All Stages");
                        setSelectedDifficulty("All Difficulties");
                        setPriceFilter("All");
                        setStructureTypeFilter("All Types");
                        setBadgeFilter("All");
                        setSearchTerm("");
                      }}
                      className="text-[10px] text-[#7C3AED] hover:text-[#6D28D9] font-bold"
                    >
                      Reset All
                    </button>
                  )}
                </div>

                {/* Price Tier */}
                <div className="mb-4">
                  <label className="text-[10px] font-bold text-slate-400 block mb-2 uppercase tracking-wider">Price Tier</label>
                  <div className="grid grid-cols-3 gap-1 bg-slate-100/70 p-1 rounded-xl">
                    {["All", "Free", "Premium"].map((price) => (
                      <button
                        key={price}
                        onClick={() => setPriceFilter(price)}
                        className={`py-1.5 text-[10px] font-bold rounded-lg text-center transition ${
                          priceFilter === price
                            ? "bg-white text-[#7C3AED] shadow-xs"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {price}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Badges Filter */}
                <div className="mb-4">
                  <label className="text-[10px] font-bold text-slate-400 block mb-2 uppercase tracking-wider">Template Tag</label>
                  <div className="grid grid-cols-2 gap-1 bg-slate-100/70 p-1 rounded-xl">
                    {["All", "Popular", "New", "Featured"].map((b) => (
                      <button
                        key={b}
                        onClick={() => setBadgeFilter(b)}
                        className={`py-1 text-[10px] font-bold rounded-lg text-center transition ${
                          badgeFilter === b
                            ? "bg-white text-[#7C3AED] shadow-xs"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stage dropdown */}
                <div className="mb-4">
                  <label className="text-[10px] font-bold text-slate-400 block mb-2 uppercase tracking-wider">Startup Stage</label>
                  <select
                    value={selectedStage}
                    onChange={(e) => setSelectedStage(e.target.value)}
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED]"
                  >
                    {STAGES.map((stg) => (
                      <option key={stg} value={stg}>{stg}</option>
                    ))}
                  </select>
                </div>

                {/* Complexity dropdown */}
                <div className="mb-4">
                  <label className="text-[10px] font-bold text-slate-400 block mb-2 uppercase tracking-wider">Complexity</label>
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED]"
                  >
                    {DIFFICULTIES.map((diff) => (
                      <option key={diff} value={diff}>{diff}</option>
                    ))}
                  </select>
                </div>

                {/* Layout Structure dropdown */}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 block mb-2 uppercase tracking-wider">Layout Structure</label>
                  <select
                    value={structureTypeFilter}
                    onChange={(e) => setStructureTypeFilter(e.target.value)}
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED]"
                  >
                    {["All Types", "Slides", "Canvas", "Document", "List"].map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* AI Promo Box */}
              <div className="bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white rounded-3xl p-5 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-28 h-28 bg-white/10 rounded-full blur-xl pointer-events-none" />
                <Sparkles className="w-5 h-5 text-white/90 mb-3" />
                <p className="font-display font-semibold text-sm">Custom AI Blueprint?</p>
                <p className="text-[11px] text-white/80 mt-1 leading-normal">
                  Generate tailored slide presentations directly for your market segment using our AI engine.
                </p>
              </div>
            </div>

            {/* Main Template Listings Grid */}
            <div className="lg:col-span-3 space-y-6">
              
              {/* Header bar: count, view switcher & sort */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-sm text-slate-800">
                    {selectedCategory === "All Templates" ? "All Templates" : selectedCategory}
                  </h3>
                  <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                    Showing <span className="text-slate-800 font-bold">{totalCount}</span> templates
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-slate-100 p-1 rounded-xl">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-1.5 rounded-lg transition ${viewMode === "grid" ? "bg-white text-[#7C3AED] shadow-xs" : "text-slate-400"}`}
                      title="Grid View"
                    >
                      <Grid className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode("masonry")}
                      className={`p-1.5 rounded-lg transition ${viewMode === "masonry" ? "bg-white text-[#7C3AED] shadow-xs" : "text-slate-400"}`}
                      title="Masonry View"
                    >
                      <AlignLeft className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Sort dropdown */}
                  <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-xl border border-slate-200 text-xs shadow-xs">
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-transparent font-semibold outline-none text-slate-700 cursor-pointer text-xs"
                    >
                      <option value="Most Popular">Most Popular</option>
                      <option value="Most Liked">Most Liked</option>
                      <option value="Highest Rated">Highest Rated</option>
                      <option value="Newest">Newest</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Template Cards Grid */}
              {totalCount === 0 ? (
                <div className="rounded-3xl border-2 border-dashed border-slate-200 p-12 text-center bg-white">
                  <LayoutGrid className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="font-display font-semibold text-base text-slate-800">No templates found</p>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto mt-1 leading-normal">
                    Try broadening your search term or clearing filter parameters.
                  </p>
                </div>
              ) : (
                <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" : "columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6"}>
                  {filteredTemplates.slice(0, visibleCount).map((tmpl) => (
                    <div 
                      key={tmpl.id} 
                      onClick={() => handleOpenDetail(tmpl)}
                      className="bg-white border border-slate-200/90 rounded-3xl p-5 flex flex-col justify-between shadow-sm hover:shadow-2xl hover:border-purple-300 hover:-translate-y-1.5 transition-all duration-300 relative group cursor-pointer overflow-hidden break-inside-avoid h-full"
                    >
                      {/* Badge Ribbons */}
                      <div className="absolute top-3.5 right-3.5 flex gap-1.5 z-10">
                        {tmpl.isPopular && (
                          <span className="text-[9px] font-extrabold uppercase tracking-wider bg-blue-600 text-white px-2 py-0.5 rounded-full shadow-xs">Popular</span>
                        )}
                        {tmpl.isNew && (
                          <span className="text-[9px] font-extrabold uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded-full shadow-xs">New</span>
                        )}
                        <span className={`text-[9px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-xs ${
                          tmpl.price === "Free" ? "bg-emerald-600 text-white" : "bg-purple-600 text-white"
                        }`}>
                          {tmpl.price}
                        </span>
                      </div>

                      {/* Header & Large Image */}
                      <div>
                        <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden border border-slate-200/80 relative mb-4 bg-slate-950 shadow-inner flex items-center justify-center">
                          <TemplateImage 
                            src={tmpl.previewImage} 
                            title={tmpl.title} 
                            category={tmpl.category} 
                            structureType={tmpl.structureType} 
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-extrabold text-[#7C3AED] uppercase bg-purple-50 px-2.5 py-0.5 rounded-full border border-purple-100">
                            {tmpl.category}
                          </span>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <span className="font-bold text-slate-700">{tmpl.rating ? tmpl.rating.toFixed(1) : "4.9"}</span>
                          </div>
                        </div>

                        <h4 className="font-bold text-sm text-slate-900 mt-2.5 line-clamp-1 group-hover:text-[#7C3AED] transition">
                          {tmpl.title}
                        </h4>
                        <p className="text-slate-500 text-xs mt-1.5 line-clamp-2 leading-relaxed font-normal">
                          {tmpl.description}
                        </p>
                      </div>

                      {/* Action Footer */}
                      <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3 text-[10px] text-slate-400 font-medium">
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3 text-slate-400" /> {(tmpl.downloads || 0).toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className={`w-3 h-3 ${likedIds.has(tmpl.id) ? "text-rose-500 fill-rose-500" : "text-slate-400"}`} /> {(tmpl.likes || 0).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenPreview(tmpl); }}
                            className="bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-xl transition border border-slate-200"
                          >
                            Quick Preview
                          </button>
                          
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUseTemplate(tmpl); }}
                            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold px-3.5 py-1.5 rounded-xl transition shadow-xs"
                          >
                            Use Template
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Pagination */}
              {totalCount > visibleCount && (
                <div className="text-center pt-4">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 12)}
                    className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold px-7 py-3 rounded-2xl transition shadow-xs inline-flex items-center gap-2"
                  >
                    Load More Blueprints ({totalCount - visibleCount} remaining)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 4. MY SAVED COPIES SECTION */}
          {user && customTemplates.length > 0 && (
            <div className="w-full px-4 sm:px-6 md:px-8 mt-16 max-w-7xl mx-auto">
              <h3 className="font-display font-bold text-xl text-slate-900 mb-6 flex items-center gap-2">
                <FolderHeart className="w-5 h-5 text-[#7C3AED]" /> My Saved Templates ({customTemplates.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {customTemplates.map((tmpl) => (
                  <div 
                    key={tmpl.id} 
                    onClick={() => handleOpenEditor(tmpl)}
                    className="bg-white border border-slate-200 rounded-3xl p-5 shadow-xs flex flex-col justify-between text-left relative hover:shadow-md transition cursor-pointer"
                  >
                    <div>
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold text-[#7C3AED] uppercase bg-purple-50 px-2 py-0.5 rounded-full">{tmpl.category}</span>
                        <span className="text-[10px] text-slate-400 font-medium">v{tmpl.version || "1.0"}</span>
                      </div>
                      <h4 className="font-bold text-sm text-slate-900 mt-2.5">{tmpl.title}</h4>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{tmpl.description}</p>
                    </div>

                    <div className="mt-5 pt-3 border-t border-slate-100 flex justify-between items-center">
                      <span className="text-[10px] text-slate-400">Updated {tmpl.lastUpdated}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenEditor(tmpl); }}
                          className="bg-purple-50 hover:bg-purple-100 text-[#7C3AED] text-xs font-bold rounded-xl px-3 py-1.5 transition"
                        >
                          Continue Editing
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteCustomTemplate(tmpl.id); }}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 border border-transparent rounded-xl transition"
                          title="Delete copy"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* 5. DEDICATED TEMPLATE DETAILS MODAL / VIEW WITH CONTINUOUS NAVIGATION */}
      <AnimatePresence mode="wait">
        {detailTemplate && (
          <motion.div 
            key={`detail-modal-${detailTemplate.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black/65 backdrop-blur-sm flex items-center justify-center p-2 md:p-6"
          >
            <motion.div 
              initial={{ scale: 0.96, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 20 }}
              className="bg-white border border-slate-200 text-slate-900 shadow-2xl w-full max-w-5xl rounded-3xl overflow-hidden flex flex-col max-h-[92vh]"
            >
              {/* Header with Navigation Controls */}
              <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDetailGoBack}
                    className="p-2 text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition inline-flex items-center gap-1.5 text-xs font-bold mr-1 border border-slate-200"
                    title={detailHistory.length > 0 ? "Go back to previous template" : "Return to Templates Marketplace"}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{detailHistory.length > 0 ? "Back" : "Marketplace"}</span>
                  </button>

                  <span className="text-[10px] uppercase font-black tracking-widest text-[#7C3AED] bg-purple-50 px-3 py-1 rounded-full border border-purple-100">
                    {detailTemplate.category}
                  </span>
                  <h2 className="font-display font-extrabold text-base md:text-xl text-slate-900 line-clamp-1">{detailTemplate.title}</h2>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Next / Previous Controls */}
                  <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl mr-2">
                    <button
                      onClick={handlePrevDetail}
                      disabled={!hasPrevDetail}
                      className="px-2 py-1 text-slate-700 hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition text-xs font-bold flex items-center gap-1"
                      title="Previous template in list"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Prev
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono px-1">
                      {currentDetailIndex + 1}/{filteredTemplates.length}
                    </span>
                    <button
                      onClick={handleNextDetail}
                      disabled={!hasNextDetail}
                      className="px-2 py-1 text-slate-700 hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition text-xs font-bold flex items-center gap-1"
                      title="Next template in list"
                    >
                      Next <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <button
                    onClick={() => toggleBookmark(detailTemplate.id)}
                    className="p-2 text-slate-400 hover:text-[#7C3AED] rounded-xl hover:bg-purple-50 transition"
                    title="Bookmark Template"
                  >
                    <Bookmark className={`w-4 h-4 ${bookmarkedIds.has(detailTemplate.id) ? "fill-[#7C3AED] text-[#7C3AED]" : ""}`} />
                  </button>
                  <button
                    onClick={() => handleShareTemplate(detailTemplate)}
                    className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition"
                    title="Share Template"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCloseDetail}
                    className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-grow overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Left Preview Slot / Multi-Slide Switcher */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden border border-slate-200 shadow-md relative bg-slate-950 group">
                    <div 
                      className="w-full h-full transition-transform duration-200 flex items-center justify-center"
                      style={{ transform: `scale(${detailZoomScale})` }}
                    >
                      {currentDetailSlide ? (
                        renderSlideCanvas(currentDetailSlide, safeDetailIndex, detailSlides.length, detailTemplate)
                      ) : (
                        <TemplateImage 
                          src={detailTemplate.previewImage} 
                          title={detailTemplate.title} 
                          category={detailTemplate.category} 
                          structureType={detailTemplate.structureType} 
                        />
                      )}
                    </div>

                    {/* Interactive Zoom Toolbar Overlay */}
                    <div className="absolute bottom-3 right-3 bg-slate-900/85 backdrop-blur-md border border-slate-700/80 rounded-xl p-1 flex items-center gap-1 z-20 text-white shadow-lg">
                      <button 
                        onClick={() => setDetailZoomScale(z => Math.max(0.75, z - 0.25))}
                        className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 hover:text-white transition"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-[10px] font-mono px-1 text-slate-300 font-bold">
                        {Math.round(detailZoomScale * 100)}%
                      </span>
                      <button 
                        onClick={() => setDetailZoomScale(z => Math.min(2.5, z + 0.25))}
                        className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 hover:text-white transition"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => setDetailZoomScale(1.0)}
                        className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 hover:text-white transition border-l border-slate-700 pl-1.5 ml-0.5"
                        title="Reset Zoom"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* High-Resolution Multi-Slide Thumbnail Row */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Slide Previews ({detailSlides.length})</p>
                    <div className="flex gap-2.5 overflow-x-auto pb-2 no-scrollbar">
                      {detailSlides.map((slide, idx) => {
                        const isSelected = safeDetailIndex === idx;
                        const thumbImg = (typeof slide.image === "string" && slide.image.startsWith("/"))
                          ? slide.image
                          : (detailTemplate?.galleryImages?.[idx] || detailTemplate?.previewImage || "/templates/previews/studlyf-master-deck.png");

                        return (
                          <button
                            key={idx}
                            onClick={() => setActiveSlideIndex(idx)}
                            className={`w-28 aspect-[16/10] rounded-xl overflow-hidden border shrink-0 transition relative ${
                              isSelected 
                                ? "border-[#7C3AED] ring-2 ring-purple-300 shadow-md scale-102" 
                                : "border-slate-200 opacity-70 hover:opacity-100"
                            }`}
                          >
                            <img src={thumbImg} alt={slide.title} className="w-full h-full object-cover" />
                            <span className="absolute bottom-1 right-1 text-[8px] font-bold bg-slate-900/85 text-white px-1.5 py-0.5 rounded">
                              Slide {idx + 1}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stats Bar */}
                  <div className="grid grid-cols-4 gap-2 bg-slate-50 p-3 rounded-2xl text-center border border-slate-100">
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Downloads</p>
                      <p className="text-xs font-black text-slate-800">{detailTemplate.downloads || 0}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Rating</p>
                      <p className="text-xs font-black text-amber-500 flex items-center justify-center gap-0.5">
                        <Star className="w-3 h-3 fill-current" /> {detailTemplate.rating || 4.9}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Likes</p>
                      <p className="text-xs font-black text-rose-500">{detailTemplate.likes || 0}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">Version</p>
                      <p className="text-xs font-black text-slate-800">v{detailTemplate.version || "1.0"}</p>
                    </div>
                  </div>
                </div>

                {/* Right Metadata & Details */}
                <div className="lg:col-span-5 space-y-6">
                  <div>
                    <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-2">About Blueprint</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{detailTemplate.description}</p>
                  </div>

                  {/* Features List */}
                  {detailTemplate.features && (
                    <div>
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-2">Key Highlights</h4>
                      <ul className="space-y-2">
                        {detailTemplate.features.map((feat, fIdx) => (
                          <li key={fIdx} className="text-xs text-slate-700 flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Creator Info */}
                  <div className="bg-slate-50 border border-slate-200/80 p-4 rounded-2xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#7C3AED] text-white flex items-center justify-center font-bold text-xs">
                        {(detailTemplate.creator || "ST")[0]}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-900">{detailTemplate.creator || "STUDLYF Team"}</p>
                        <p className="text-[10px] text-slate-400 font-medium">Verified Blueprint Creator</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400">Updated {detailTemplate.lastUpdated || "2026"}</span>
                  </div>

                  {/* Reviews Sample */}
                  {detailTemplate.reviews && (
                    <div>
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-2">Community Reviews</h4>
                      <div className="space-y-2">
                        {detailTemplate.reviews.map((rev, rIdx) => (
                          <div key={rIdx} className="bg-white border border-slate-150 p-3 rounded-xl shadow-2xs">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-bold text-slate-800">{rev.user}</span>
                              <div className="flex text-amber-400">
                                {[...Array(rev.rating)].map((_, i) => (
                                  <Star key={i} className="w-3 h-3 fill-current" />
                                ))}
                              </div>
                            </div>
                            <p className="text-[11px] text-slate-500">{rev.comment}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Related & Similar Templates Row */}
                {relatedTemplates.length > 0 && (
                  <div className="lg:col-span-12 pt-6 border-t border-slate-100 mt-2">
                    <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-800 mb-4 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#7C3AED]" /> Related & Similar Blueprints
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      {relatedTemplates.map((relTmpl) => (
                        <div
                          key={relTmpl.id}
                          onClick={() => handleOpenDetail(relTmpl, true)}
                          className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3 shadow-2xs hover:shadow-md transition cursor-pointer flex flex-col justify-between"
                        >
                          <div>
                            <div className="w-full aspect-[16/10] rounded-xl overflow-hidden mb-2.5 border border-slate-200">
                              <TemplateImage src={relTmpl.previewImage} title={relTmpl.title} category={relTmpl.category} structureType={relTmpl.structureType} />
                            </div>
                            <span className="text-[8px] font-bold text-[#7C3AED] uppercase bg-purple-50 px-2 py-0.5 rounded-full">{relTmpl.category}</span>
                            <h5 className="font-bold text-xs text-slate-900 mt-1 line-clamp-1">{relTmpl.title}</h5>
                            <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{relTmpl.description}</p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenDetail(relTmpl, true); }}
                            className="mt-3 w-full bg-white hover:bg-purple-50 text-[#7C3AED] border border-purple-200 text-[10px] font-bold rounded-xl py-1.5 transition text-center"
                          >
                            Open Blueprint
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* Footer Actions with Prev/Next Buttons */}
              <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevDetail}
                    disabled={!hasPrevDetail}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1 transition border bg-white border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  <button
                    onClick={handleNextDetail}
                    disabled={!hasNextDetail}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1 transition border bg-white border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleLike(detailTemplate.id)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition border ${
                      likedIds.has(detailTemplate.id)
                        ? "bg-rose-50 border-rose-200 text-rose-600"
                        : "bg-white border-slate-200 text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Heart className={`w-3.5 h-3.5 ${likedIds.has(detailTemplate.id) ? "fill-current text-rose-500" : ""}`} />
                    {likedIds.has(detailTemplate.id) ? "Favorited" : "Favorite"}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(detailTemplate, "PDF")}
                    className="bg-white hover:bg-slate-100 text-slate-800 font-bold px-3.5 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition border border-slate-200"
                  >
                    <Download className="w-3.5 h-3.5" /> Save PDF
                  </button>
                  <button
                    onClick={() => handleOpenPreview(detailTemplate)}
                    className="bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold px-4 py-2 rounded-xl transition border border-slate-200"
                  >
                    Full Screen Preview
                  </button>
                  <button
                    onClick={() => handleUseTemplate(detailTemplate)}
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold px-6 py-2 rounded-xl transition shadow-sm inline-flex items-center gap-1"
                  >
                    Use Blueprint <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. FULL SCREEN PREVIEW MODAL */}
      <AnimatePresence mode="wait">
        {previewTemplate && previewSlides.length > 0 && (
          <motion.div 
            key={`preview-modal-${previewTemplate.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden bg-black/75 backdrop-blur-xs flex items-center justify-center p-0 md:p-4"
          >
            <motion.div 
              initial={{ scale: 0.97, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.97, y: 16 }}
              className={`bg-slate-900 border border-slate-800 text-white flex flex-col justify-between shadow-2xl relative overflow-hidden transition-all duration-300 ${
                isPreviewWorkspaceFullscreen 
                  ? "w-screen h-screen rounded-none" 
                  : "max-w-6xl w-full h-full md:h-[88vh] rounded-none md:rounded-3xl"
              }`}
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0 bg-slate-900">
                <div className="flex items-center gap-3">
                  <span className="text-[9px] uppercase font-black tracking-widest text-[#7C3AED] bg-purple-900/40 px-2 py-0.5 rounded border border-purple-800">
                    {previewTemplate.category}
                  </span>
                  <h2 className="font-bold text-sm text-slate-100">{previewTemplate.title}</h2>
                  <span className="text-[10px] text-slate-400">({previewSlides.length} Slides)</span>
                </div>
                
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setIsPreviewWorkspaceFullscreen(!isPreviewWorkspaceFullscreen)}
                    className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
                  >
                    {isPreviewWorkspaceFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleClosePreview}
                    className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-grow flex overflow-hidden">
                <div className="w-48 bg-slate-950 border-r border-slate-800 p-3 overflow-y-auto hidden md:block shrink-0 no-scrollbar">
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-3">Slide Index</p>
                  <div className="space-y-2">
                    {previewSlides.map((slide, idx) => (
                      <div 
                        key={idx}
                        onClick={() => setActiveSlideIndex(idx)}
                        className={`cursor-pointer rounded-xl p-2 border transition relative aspect-video flex flex-col justify-between text-left overflow-hidden ${
                          safePreviewIndex === idx 
                            ? "border-[#7C3AED] bg-slate-900 shadow-sm" 
                            : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
                        }`}
                      >
                        <span className="absolute top-1 left-1.5 text-[8px] font-bold text-slate-500">
                          {idx + 1}
                        </span>
                        <div className="flex-grow flex items-center justify-center p-1 text-center">
                          <p className="text-[8px] font-bold text-slate-300 line-clamp-2 leading-tight">
                            {slide.title}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-grow p-6 flex flex-col items-center justify-center bg-slate-950 relative overflow-auto">
                  <div 
                    className="w-full max-w-4xl lg:max-w-5xl aspect-[16/9] transition-transform duration-200 select-none shadow-2xl rounded-2xl overflow-hidden"
                    style={{ transform: `scale(${zoomScale})` }}
                  >
                    {renderSlideCanvas(currentPreviewSlide, safePreviewIndex, previewSlides.length)}
                  </div>

                  <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
                    <button
                      onClick={() => setActiveSlideIndex(i => Math.max(0, i - 1))}
                      disabled={safePreviewIndex === 0}
                      className="p-2.5 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700 text-white disabled:opacity-40 transition pointer-events-auto shadow-md"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setActiveSlideIndex(i => Math.min(previewSlides.length - 1, i + 1))}
                      disabled={safePreviewIndex === previewSlides.length - 1}
                      className="p-2.5 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700 text-white disabled:opacity-40 transition pointer-events-auto shadow-md"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-800 bg-slate-900 flex flex-wrap items-center justify-between gap-4 shrink-0 text-slate-400 text-xs">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-slate-200">
                    Slide {safePreviewIndex + 1} of {previewSlides.length}
                  </span>
                  
                  <div className="flex items-center gap-1.5 border-l border-slate-800 pl-3">
                    <button 
                      onClick={() => setZoomScale(s => Math.max(0.6, s - 0.1))}
                      className="p-1 hover:text-white transition"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-[10px] text-slate-500 font-mono w-8 text-center">
                      {Math.round(zoomScale * 100)}%
                    </span>
                    <button 
                      onClick={() => setZoomScale(s => Math.min(1.4, s + 0.1))}
                      className="p-1 hover:text-white transition"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleDownload(previewTemplate, "PDF")}
                    className="bg-white hover:bg-slate-100 text-slate-900 font-bold px-3 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition"
                  >
                    <Download className="w-3.5 h-3.5" /> Save PDF
                  </button>
                  <button 
                    onClick={() => handleDownload(previewTemplate, "PPTX")}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold px-3 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition border border-slate-700"
                  >
                    <Download className="w-3.5 h-3.5 text-purple-400" /> Export PPTX
                  </button>
                  <button
                    onClick={() => handleUseTemplate(previewTemplate)}
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold px-4 py-2 rounded-xl inline-flex items-center gap-1 transition shadow-sm ml-2"
                  >
                    Use Template <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 7. PRESENTER WORKSPACE EDITOR MODAL */}
      <AnimatePresence mode="wait">
        {editingTemplate && editorSlides.length > 0 && (
          <motion.div 
            key={`editor-modal-${editingTemplate.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden bg-black/75 backdrop-blur-xs flex items-center justify-center p-0 md:p-4"
          >
            <motion.div 
              initial={{ scale: 0.97, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.97, y: 16 }}
              className="bg-slate-900 border border-slate-800 text-white flex flex-col justify-between shadow-2xl w-screen h-screen md:h-[90vh] md:w-full md:max-w-6xl rounded-none md:rounded-3xl overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0 bg-slate-900">
                <div className="flex items-center gap-3">
                  <h2 className="font-bold text-sm text-slate-100">STUDLYF Presenter Workspace</h2>
                  <span className="text-[10px] text-slate-400">Editing Copy: {editingTemplate.title}</span>
                </div>
                <button
                  onClick={handleCloseEditor}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-grow flex overflow-hidden">
                <div className="w-44 bg-slate-950 border-r border-slate-800 p-3 overflow-y-auto shrink-0 no-scrollbar">
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-3">Outline</p>
                  <div className="space-y-2">
                    {editorSlides.map((slide, idx) => (
                      <div 
                        key={idx}
                        onClick={() => setActiveSlideIndex(idx)}
                        className={`cursor-pointer rounded-xl p-2 border transition relative aspect-video flex flex-col justify-between text-left overflow-hidden ${
                          safeEditorIndex === idx 
                            ? "border-[#7C3AED] bg-slate-900 shadow-sm" 
                            : "border-slate-800 bg-slate-955/40 hover:border-slate-700"
                        }`}
                      >
                        <span className="absolute top-1 left-1.5 text-[8px] font-bold text-slate-500">
                          {idx + 1}
                        </span>
                        <div className="flex-grow flex items-center justify-center p-1 text-center">
                          <p className="text-[8px] font-bold text-slate-300 line-clamp-2 leading-tight">
                            {slide.title}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-grow p-6 flex flex-col items-center justify-center bg-slate-955 relative overflow-auto">
                  <div 
                    className="w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl aspect-[16/9] transition-transform duration-200 select-none shadow-xl shrink-0 rounded-2xl overflow-hidden"
                    style={{ transform: `scale(${zoomScale})` }}
                  >
                    {renderSlideCanvas(currentEditorSlide, safeEditorIndex, editorSlides.length)}
                  </div>
                  
                  <div className="mt-4 bg-slate-900/90 border border-slate-800 rounded-full px-3 py-1 flex items-center gap-3 text-[10px] text-slate-400">
                    <button 
                      onClick={() => setActiveSlideIndex(idx => Math.max(0, idx - 1))}
                      disabled={safeEditorIndex === 0}
                      className="hover:text-white transition disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <span className="font-bold text-slate-200">
                      Slide {safeEditorIndex + 1} of {editorSlides.length}
                    </span>
                    <button 
                      onClick={() => setActiveSlideIndex(idx => Math.min(editorSlides.length - 1, idx + 1))}
                      disabled={safeEditorIndex === editorSlides.length - 1}
                      className="hover:text-white transition disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* Right Form Editor */}
                <div className="w-72 bg-slate-900 border-l border-slate-800 p-4 overflow-y-auto shrink-0 space-y-4">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pb-2 border-b border-slate-800">
                    Slide Editor Content
                  </p>
                  
                  <div className="bg-purple-955/20 border border-purple-900/30 rounded-xl p-3 flex items-start gap-2">
                    <Info className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                    <p className="text-[9px] text-slate-400 leading-normal">
                      Master structure & formatting bounds are locked for compliance. Edit content fields below.
                    </p>
                  </div>

                  {currentEditorSlide && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase">Category / Subtitle</label>
                        <input
                          type="text"
                          value={currentEditorSlide.subtitle || ""}
                          onChange={(e) => handleSlideTextChange("subtitle", e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED] text-xs text-slate-900 font-semibold transition"
                          placeholder="Subtitle / Category..."
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase">Slide Title</label>
                        <input
                          type="text"
                          value={currentEditorSlide.title || ""}
                          onChange={(e) => handleSlideTextChange("title", e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED] text-xs text-slate-900 font-semibold transition"
                          placeholder="Enter slide title..."
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-bold text-slate-400 block mb-1 uppercase">Content / Bullets</label>
                        <textarea
                          value={currentEditorSlide.bullets || ""}
                          onChange={(e) => handleSlideTextChange("bullets", e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 outline-none focus:border-[#7C3AED] text-xs text-slate-900 min-h-[110px] leading-relaxed transition"
                          placeholder="Enter detail text..."
                        />
                      </div>

                      {/* Columns form controls */}
                      {currentEditorSlide.columns && currentEditorSlide.columns.length > 0 && (
                        <div className="space-y-3 pt-2 border-t border-slate-800">
                          <label className="text-[10px] font-bold text-purple-400 block uppercase">Columns ({currentEditorSlide.columns.length})</label>
                          {currentEditorSlide.columns.map((col, cIdx) => (
                            <div key={cIdx} className="space-y-1.5 bg-slate-950 p-2.5 rounded-xl border border-slate-800">
                              <input
                                type="text"
                                value={col.title || ""}
                                onChange={(e) => handleSlideTextChange("column-title", e.target.value, cIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-900"
                                placeholder={`Column ${cIdx + 1} Title`}
                              />
                              <textarea
                                value={col.content || ""}
                                onChange={(e) => handleSlideTextChange("column-content", e.target.value, cIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900 min-h-[50px]"
                                placeholder={`Column ${cIdx + 1} Content`}
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Metrics form controls */}
                      {currentEditorSlide.metrics && currentEditorSlide.metrics.length > 0 && (
                        <div className="space-y-3 pt-2 border-t border-slate-800">
                          <label className="text-[10px] font-bold text-purple-400 block uppercase">Metrics ({currentEditorSlide.metrics.length})</label>
                          {currentEditorSlide.metrics.map((m, mIdx) => (
                            <div key={mIdx} className="space-y-1.5 bg-slate-950 p-2.5 rounded-xl border border-slate-800">
                              <input
                                type="text"
                                value={m.value || ""}
                                onChange={(e) => handleSlideTextChange("metric-value", e.target.value, mIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-black text-slate-900"
                                placeholder="Metric Value"
                              />
                              <input
                                type="text"
                                value={m.label || ""}
                                onChange={(e) => handleSlideTextChange("metric-label", e.target.value, mIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-semibold text-slate-900 uppercase"
                                placeholder="Metric Label"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Timeline form controls */}
                      {currentEditorSlide.timeline && currentEditorSlide.timeline.length > 0 && (
                        <div className="space-y-3 pt-2 border-t border-slate-800">
                          <label className="text-[10px] font-bold text-purple-400 block uppercase">Timeline Items ({currentEditorSlide.timeline.length})</label>
                          {currentEditorSlide.timeline.map((tItem, tIdx) => (
                            <div key={tIdx} className="space-y-1.5 bg-slate-950 p-2.5 rounded-xl border border-slate-800">
                              <input
                                type="text"
                                value={tItem.label || ""}
                                onChange={(e) => handleSlideTextChange("timeline-label", e.target.value, tIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-900"
                                placeholder="Phase Label"
                              />
                              <input
                                type="text"
                                value={tItem.description || ""}
                                onChange={(e) => handleSlideTextChange("timeline-desc", e.target.value, tIdx)}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs text-slate-900"
                                placeholder="Phase Description"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-800 bg-slate-900 flex flex-wrap justify-between items-center shrink-0 gap-3">
                <span className="text-[10px] text-slate-400 font-mono">
                  Locked Layout: STUDLYF Pro Builder
                </span>
                
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleSaveAndDownloadInEditor("PPTX")}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold px-3 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition border border-slate-700"
                  >
                    <Download className="w-3.5 h-3.5 text-purple-400" /> Download PPT
                  </button>
                  <button
                    onClick={() => handleSaveAndDownloadInEditor("PDF")}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold px-3 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition border border-slate-700"
                  >
                    <Download className="w-3.5 h-3.5" /> Save PDF
                  </button>
                  <button
                    onClick={() => handleSaveAndDownloadInEditor("DOCX")}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold px-3 py-2 rounded-xl text-xs inline-flex items-center gap-1.5 transition border border-slate-700"
                  >
                    <Download className="w-3.5 h-3.5" /> Export DOCX
                  </button>

                  <div className="h-4 w-px bg-slate-800 mx-1 hidden sm:block" />

                  <button
                    onClick={handleCloseEditor}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold px-4 py-2 rounded-xl transition border border-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      saveCustomTemplateState(editingTemplate);
                      handleCloseEditor();
                      toast.success("Saved template copy successfully!");
                    }}
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold px-5 py-2 rounded-xl transition shadow-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 8. PRINT PRESENTATION SCREEN CONTAINER FOR PDF EXPORTS */}
      {(previewTemplate || editingTemplate || detailTemplate) && (
        <div id="print-presentation-container" className="hidden">
          {(previewTemplate ? previewSlides : editingTemplate ? editorSlides : detailSlides).map((slide, idx) => (
            <div key={idx} className="print-slide-page relative overflow-hidden">
              <div className="w-[297mm] h-[167mm]">
                {renderSlideCanvas(slide, idx, (previewTemplate ? previewSlides : editingTemplate ? editorSlides : detailSlides).length)}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}